import { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import SectionLabel from '../common/SectionLabel';
import { formatCurrency } from '../../utils/formatting';
import { ACTUALS_CUTOFF_2026 } from '../../context/FinancialDataContext';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function sumCat(monthData, category) {
  if (!monthData?.[category]) return 0;
  return Object.values(monthData[category]).reduce((s, v) => s + (v || 0), 0);
}

export default function RevenueForecastChart({ global2026Monthly, priorYearData }) {
  const chartData = useMemo(() => {
    if (!global2026Monthly?.months) return [];

    return MONTH_KEYS.map((mk, i) => {
      const income2026 = sumCat(global2026Monthly.months[mk], 'income');
      const cogs2026 = sumCat(global2026Monthly.months[mk], 'cogs');
      const expenses2026 = sumCat(global2026Monthly.months[mk], 'expenses');
      const noi2026 = income2026 - cogs2026 - expenses2026;

      const income2025 = priorYearData ? sumCat(priorYearData.months?.[mk], 'income') : 0;

      return {
        month: MONTH_LABELS[i],
        actual: i < ACTUALS_CUTOFF_2026 ? income2026 : null,
        projected: i >= ACTUALS_CUTOFF_2026 ? income2026 : null,
        prior: income2025,
        noi: noi2026,
        isActual: i < ACTUALS_CUTOFF_2026,
      };
    });
  }, [global2026Monthly, priorYearData]);

  // YTD actuals total
  const ytdActual = chartData.slice(0, ACTUALS_CUTOFF_2026).reduce((s, d) => s + (d.actual || 0), 0);
  const fullYearProjected = chartData.reduce((s, d) => s + (d.actual || 0) + (d.projected || 0), 0);

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mb-6">
      <div className="flex items-center justify-between mb-1">
        <SectionLabel>Monthly Revenue: 2026 vs 2025</SectionLabel>
        <div className="flex items-center gap-4 text-[11px]">
          <span className="text-[#888]">
            YTD Actual: <span className="font-bold text-navy">{formatCurrency(ytdActual)}</span>
          </span>
          <span className="text-[#888]">
            Full Year: <span className="font-bold text-navy">{formatCurrency(fullYearProjected)}</span>
          </span>
        </div>
      </div>
      <p className="text-[11px] font-light text-[#888] mb-4">
        Solid bars are closed actuals (Jan-Mar). Lighter bars are projections (Apr-Dec). Line shows 2025 for comparison.
      </p>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#888' }} tickLine={false} />
          <YAxis tick={{ fontSize: 12, fill: '#888' }} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
          <Tooltip
            formatter={(value, name) => [formatCurrency(value), name]}
            contentStyle={{ fontSize: 13, borderRadius: 4, border: '1px solid #e5e7eb' }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="actual" fill="#006D78" name="2026 Actual" radius={[3, 3, 0, 0]} />
          <Bar dataKey="projected" fill="#3EAAD0" name="2026 Projected" radius={[3, 3, 0, 0]} />
          <Line type="monotone" dataKey="prior" stroke="#022889" name="2025 Actual" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3, fill: '#022889' }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
